import type { CustomDeckGroup, CustomDeckPage, Page } from '@alucio/aws-beacon-amplify/src/models';
import type { CustomDeckORM, DocumentVersionORM } from 'src/types/orms';
import type { GroupDraft } from 'src/components/SlideSelector/PageGroupList';
import type { ThumbnailPage } from 'src/components/SlideSelector/SlideSelector';
import type {
  PayloadGroup, ModifiedPayloadGroup,
} from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { getCustomDeckDefaultGroupVisibility } from '../hiddenSlides';
import type { ValidationDependencies } from './validation';
import { GroupStatus } from './groupStatus';

export const getAssociatedParentIds = (pages: readonly Pick<Page, 'pageId' | 'linkedSlides'>[]) => {
  const parents = new Map<string, string[]>();
  pages.forEach(page => {
    page.linkedSlides?.forEach(slideId => {
      const parentIds = parents.get(slideId) ?? [];
      parentIds.push(page.pageId);
      parents.set(slideId, parentIds);
    });
  });
  return parents;
};

export const materializeDraftGroups = (
  groupDrafts: GroupDraft[],
  pages: ThumbnailPage[],
  selectedDocumentVersionORM: DocumentVersionORM,
  dependencies: ValidationDependencies,
): ModifiedPayloadGroup[] => {
  const { createId, getPageWithDocumentVersionHiddenState } = dependencies;
  const allAssociatedParents = getAssociatedParentIds(pages);

  const result = groupDrafts.map(group => {
    const groupId = createId();
    const newPayloadPages: ThumbnailPage[]  = [];
    group.pages.forEach((page) => {
      const pageWithDocumentHiddenState = getPageWithDocumentVersionHiddenState(
        Object.assign({}, page),
        selectedDocumentVersionORM,
      );
      const newPayloadPage = page.isHidden === true
        ? { ...pageWithDocumentHiddenState, isHidden: true }
        : pageWithDocumentHiddenState;
      newPayloadPage.parentIds = [...(allAssociatedParents.get(page.pageId) ?? [])];
      newPayloadPages.push(newPayloadPage);
    });

    const mappedPayloadGroup: ModifiedPayloadGroup = {
      documentVersionORM: selectedDocumentVersionORM,
      id: groupId,
      groupSrcId: group.id,
      groupStatus: GroupStatus.ACTIVE,
      pages: newPayloadPages,
      visible: getCustomDeckDefaultGroupVisibility(newPayloadPages),
      docAccessLevel: selectedDocumentVersionORM?.relations.documentORM.model.accessLevel!,
      name: group.name,
      locked: !!group.locked,
    };

    return mappedPayloadGroup;
  });

  return result;
};

export const countSystemAddedSlides = (newPayloadGroups: ModifiedPayloadGroup[]): number => {
  let allRequiredNum = 0;

  newPayloadGroups.forEach(group => {
    const page = group.pages[0];
    const isSingleSlideGroup = group.pages?.length === 1;
    const isSystemAddedSlide = page && !page.checked;
    const isAssociatedSlide = !!page?.parentIds?.length;

    if (isSingleSlideGroup && isSystemAddedSlide && (page.isRequired || isAssociatedSlide)) {
      allRequiredNum++;
    }
  });
  return allRequiredNum;
};

export const payloadGroupsToCustomDeckGroups = (
  draftGroups: PayloadGroup[],
  existingCustomDeck?: CustomDeckORM,
) => {
  return draftGroups.map((draftGroup) => {
    let pages: CustomDeckPage[] = [];

    if (draftGroup.groupStatus === GroupStatus.DELETED) {
      // IF THE DOCUMENT WAS DELETED (MEANING THAT THE PAGES ARE EMPTY)
      // WE WANT TO KEEP THE REFERENCES AS THEY'RE IN THE CUSTOMDECK SO THEY'RE STILL
      // SHOWN AS REQUIRED TO BE MANUALLY REMOVED
      const savedGroup = existingCustomDeck?.model.groups.find(({ id }) => id === draftGroup.id);
      pages = savedGroup?.pages || [];
    } else {
      pages = draftGroup.pages.map((page) => ({
        pageId: page.pageId,
        pageNumber: page.number,
        documentVersionId: draftGroup.documentVersionORM?.model.id || '',
      }));
    }

    const newCustomDeckGroup:CustomDeckGroup = {
      id: draftGroup.id,
      srcId: draftGroup.groupSrcId ?? draftGroup.documentVersionORM?.model.id,
      pages,
      visible: draftGroup.visible,
      docAccessLevel: draftGroup.docAccessLevel,
      name:draftGroup.name,
      locked: draftGroup.locked,
    };
    return newCustomDeckGroup;
  });
};
